import { CartAddEvent } from '@theme/events';

/**
 * Slider de suggestions du drawer panier (maquette drawer-cart-maquette-v2).
 *
 * - Charge les cartes en AJAX via la section `ab-cart-recommendations`
 *   (recommandations Shopify : complementary, fallback related).
 * - Héritage de taille : si une ceinture est déjà au panier (data-belt-size),
 *   toute ceinture suggérée reprend automatiquement la même taille.
 * - Ajout au panier sans quitter le drawer (POST /cart/add.js + morph de section).
 */
class ABCartSuggestions extends HTMLElement {
  connectedCallback() {
    this.productsEl = this.querySelector('[data-ab-sugg-products]');
    this.navEl = this.querySelector('[data-ab-sugg-nav]');
    this.beltSize = this.dataset.beltSize || '';
    this.addEventListener('click', this.#onClick);

    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  async #load() {
    const { url, productId, sectionId } = this.dataset;
    if (!url || !productId) return this.#fail();

    const tryIntent = async (intent) => {
      const res = await fetch(`${url}?section_id=${sectionId}&product_id=${productId}&intent=${intent}&limit=10`);
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const rail = doc.querySelector('[data-ab-sugg-rail]');
      return rail && rail.children.length ? rail : null;
    };

    let rail = await tryIntent('complementary').catch(() => null);
    if (!rail) rail = await tryIntent('related').catch(() => null);
    if (!rail) return this.#fail();

    this.productsEl.innerHTML = rail.innerHTML;
    this.productsEl.removeAttribute('aria-busy');
    this.#decorateCards();
    this.#wireScroll();
    this.#updateArrows();
  }

  #fail() {
    // On retire l'élément (et pas juste display:none) : le layout desktop
    // du drawer s'élargit via :has(.ab-sugg) et doit se replier en cas d'échec.
    this.remove();
  }

  /** @param {string} value */
  #num(value) {
    const m = String(value).match(/\d+/);
    return m ? m[0] : null;
  }

  #decorateCards() {
    const cards = this.productsEl.querySelectorAll('.ab-sugg-card');

    for (const card of cards) {
      let variants = [];
      try {
        variants = JSON.parse(card.dataset.variants || '[]');
      } catch {
        variants = [];
      }

      const sizeOption = card.dataset.sizeOption || '';
      const sizeLine = card.querySelector('[data-ab-sugg-size]');
      const btn = card.querySelector('[data-ab-sugg-add]');
      let target = null;

      // Héritage de taille (ceinture déjà au panier)
      if (sizeOption && this.beltSize) {
        target = variants.find(
          (v) => v.available && v.size && (v.size === this.beltSize || this.#num(v.size) === this.#num(this.beltSize))
        );
        if (target && sizeLine) sizeLine.innerHTML = `Taille <strong>${target.size}</strong>`;
      }

      if (!target) {
        if (sizeOption) {
          const available = variants.filter((v) => v.available);
          // Plusieurs tailles possibles sans héritage → on renvoie vers la fiche produit
          if (available.length > 1) {
            card.dataset.mode = 'link';
            if (btn) btn.textContent = 'Choisir la taille';
            continue;
          }
          target = available[0];
        } else {
          target = variants.find((v) => v.available) || variants[0];
        }
      }

      if (!target) {
        card.dataset.mode = 'link';
        if (btn) btn.textContent = 'Voir le produit';
        continue;
      }

      card.dataset.mode = 'add';
      card.dataset.targetVariant = String(target.id);
    }
  }

  #onClick = (event) => {
    const arrow = event.target.closest('.ab-sugg__arrow');
    if (arrow) return this.#scroll(Number(arrow.dataset.dir));

    const btn = event.target.closest('[data-ab-sugg-add]');
    if (!btn) return;

    const card = btn.closest('.ab-sugg-card');
    if (!card) return;

    if (card.dataset.mode === 'link') {
      if (card.dataset.url) window.location.href = card.dataset.url;
      return;
    }
    if (card.dataset.mode === 'add' && !btn.disabled) this.#add(card, btn);
  };

  /**
   * @param {HTMLElement} card
   * @param {HTMLButtonElement} btn
   */
  async #add(card, btn) {
    const id = card.dataset.targetVariant;
    if (!id) return;

    btn.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          id: Number(id),
          quantity: 1,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await res.json();

      if (data.status) {
        btn.disabled = false;
        btn.textContent = 'Indisponible';
        return;
      }

      btn.classList.add('is-added');
      btn.textContent = 'Ajouté ✓';

      // Rafraîchit le drawer (le panier passe à 2 articles → -20€ + slider masqué)
      this.dispatchEvent(
        new CartAddEvent({}, 'ab-cart-suggestions', { source: 'ab-cart-suggestions', sections: data.sections })
      );
    } catch (error) {
      console.error(error);
      btn.disabled = false;
    }
  }

  /** @param {number} dir */
  #scroll(dir) {
    const card = this.productsEl.querySelector('.ab-sugg-card');
    const gap = parseFloat(getComputedStyle(this.productsEl).columnGap) || 12;
    const step = card ? card.getBoundingClientRect().width + gap : 208;
    this.productsEl.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  #wireScroll() {
    this.productsEl.addEventListener('scroll', () => this.#updateArrows(), { passive: true });
  }

  #updateArrows() {
    if (!this.navEl) return;
    const el = this.productsEl;
    const max = el.scrollWidth - el.clientWidth - 2;

    this.navEl.hidden = max <= 2;

    for (const arrow of this.navEl.querySelectorAll('.ab-sugg__arrow')) {
      const dir = Number(arrow.dataset.dir);
      if (dir < 0) arrow.toggleAttribute('disabled', el.scrollLeft <= 2);
      else arrow.toggleAttribute('disabled', el.scrollLeft >= max);
    }
  }
}

if (!customElements.get('ab-cart-suggestions')) {
  customElements.define('ab-cart-suggestions', ABCartSuggestions);
}
